import * as microsoftTeams from '@microsoft/teams-js';

export interface TeamsAuthContext {
  isInTeams: boolean;
  context: microsoftTeams.app.Context | null;
  token: string | null;
  userInfo: {
    id: string;
    name: string;
    email?: string;
  } | null;
}

let teamsAuthContext: TeamsAuthContext = {
  isInTeams: false,
  context: null,
  token: null,
  userInfo: null
};

/**
 * Initialize Microsoft Teams SDK and attempt to get auth token
 * Should be called before MSAL initialization
 */
export async function initializeTeamsAuth(): Promise<TeamsAuthContext> {
  try {
    // Initialize Teams SDK
    await microsoftTeams.app.initialize();
    
    // Get Teams context
    const context = await microsoftTeams.app.getContext();
    
    console.log('Running in Microsoft Teams environment', context);
    
    // Try to get SSO token
    const token = await getTeamsAuthToken();
    
    // Extract user info from context
    const userInfo = {
      id: context.user?.id || '',
      name: context.user?.displayName || context.user?.userPrincipalName || '',
      email: context.user?.userPrincipalName
    };
    
    teamsAuthContext = {
      isInTeams: true,
      context,
      token,
      userInfo
    };
    
    return teamsAuthContext;
  } catch (error) {
    // console.log('Not in Microsoft Teams environment or initialization failed', error);
    
    teamsAuthContext = {
      isInTeams: false,
      context: null,
      token: null,
      userInfo: null
    };
    
    return teamsAuthContext;
  }
}

/**
 * Get authentication token from Teams
 */
export async function getTeamsAuthToken(): Promise<string> {
  try {
    const token = await microsoftTeams.authentication.getAuthToken();
    console.log('Successfully retrieved Teams auth token');
    return token;
  } catch (error) {
    console.error('Failed to get Teams auth token', error);
    throw error;
  }
}

/**
 * Get current Teams auth context
 */
export function getTeamsAuthContext(): TeamsAuthContext {
  return teamsAuthContext;
}

/**
 * Parse JWT token to extract claims (basic parsing, no verification)
 */
export function parseJwtToken(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT token', error);
    return null;
  }
}

/**
 * Check if token is expired or will expire soon (within 5 minutes)
 */
function isTokenExpired(token: string): boolean {
  const claims = parseJwtToken(token);
  if (!claims || !claims.exp) {
    return true;
  }
  
  // Check if token expires within 5 minutes (300 seconds)
  const expirationTime = claims.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
  
  return expirationTime - currentTime < bufferTime;
}

/**
 * Get a fresh Teams auth token, refreshing if necessary
 * This should be called before each API request instead of using cached token
 */
export async function getFreshTeamsToken(): Promise<string | null> {
  if (!teamsAuthContext.isInTeams) {
    return null;
  }
  
  // Check if current token is still valid
  if (teamsAuthContext.token && !isTokenExpired(teamsAuthContext.token)) {
    return teamsAuthContext.token;
  }
  
  // Token is expired or missing, get a fresh one
  try {
    console.log('Refreshing Teams auth token...');
    const newToken = await getTeamsAuthToken();
    teamsAuthContext.token = newToken;
    return newToken;
  } catch (error) {
    console.error('Failed to refresh Teams auth token', error);
    return null;
  }
}
